// Iter 582 — Grup Nota (administratif) & Mockup Bersama lintas nota (Offline).
// Grup hanya pengelompokan: pembayaran, pcs, barcode, tahap & ACC tetap per nota/item.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Layers, Printer, Plus, X, Image as ImageIcon } from "lucide-react";
import { notify } from "./modal_center";
import "./nota_group.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const enc = encodeURIComponent;
const errMsg = (e) => e.response?.data?.detail || "Silakan coba lagi.";

let memo = { at: 0, data: null, p: null };
async function getMembership(force = false) {
  if (!force && memo.data && Date.now() - memo.at < 60000) return memo.data;
  if (!memo.p) {
    memo.p = axios.get(`${API}/nota-groups/membership`, { withCredentials: true })
      .then(r => { memo = { at: Date.now(), data: r.data.membership || {}, p: null }; return memo.data; })
      .catch(() => { memo.p = null; return {}; });
  }
  return memo.p;
}

function useGroupId(orderNumber) {
  const [gid, setGid] = useState(null);
  useEffect(() => {
    let alive = true;
    getMembership().then(m => { if (alive) setGid(m[orderNumber] || null); });
    return () => { alive = false; };
  }, [orderNumber]);
  return gid;
}

async function openSheet(gid, fileId) {
  try {
    const r = await axios.get(`${API}/nota-groups/${enc(gid)}/mockup-sheet.pdf`, { params: { file_id: fileId }, withCredentials: true, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { notify.error({ title: "Gagal membuat Lembar Mockup Bersama", message: errMsg(e) }); }
}

function notaStatus(n) {
  if (n.cancelled) return { cls: "ng-st-cancel", text: "Dibatalkan" };
  if (n.pcs > 0 && n.pcs_selesai_mockup === n.pcs) return { cls: "ng-st-done", text: "Selesai Mockup" };
  if (n.pcs_selesai_mockup > 0) return { cls: "ng-st-part", text: `Sebagian Selesai Mockup (${n.pcs_selesai_mockup}/${n.pcs} pcs)` };
  return { cls: "ng-st-wait", text: "Belum Selesai Mockup" };
}

function GroupTable({ group, current, onRemove }) {
  const t = group.totals;
  return (
    <div className="ng-table" data-testid="nota-group-table">
      {group.notas.map(n => {
        const st = notaStatus(n);
        return (
          <div key={n.order_number} className={`ng-row ${n.order_number === current ? "ng-row-current" : ""}`}
            data-testid={`nota-group-row-${n.order_number}`}>
            <span className="ng-nota">Nota {n.order_number}</span>
            <span className="ng-pcs" data-testid={`nota-group-pcs-${n.order_number}`}>{n.pcs} pcs</span>
            <span className={`ng-st ${st.cls}`} data-testid={`nota-group-status-${n.order_number}`}>{st.text}</span>
            {onRemove && n.order_number !== current && (
              <button type="button" className="ng-link-btn" onClick={() => onRemove(n.order_number)}
                data-testid={`nota-group-remove-${n.order_number}`}>Keluarkan</button>)}
          </div>
        );
      })}
      <div className="ng-totals" data-testid="nota-group-totals">
        <span>Total grup: <b data-testid="nota-group-total-pcs">{t.pcs} pcs</b></span>
        <span className="ng-ok">Siap diproses: <b data-testid="nota-group-ready-pcs">{t.siap_pcs} pcs</b></span>
        <span className={t.belum_pcs ? "ng-warn" : ""}>Belum Selesai Mockup: <b data-testid="nota-group-wait-pcs">{t.belum_pcs} pcs</b></span>
      </div>
      {t.belum_pcs > 0 && <p className="ng-alert" data-testid="nota-group-warning">
        Ada item yang belum Selesai Mockup — jangan diproduksi sebelum tahap Mockup nota tersebut selesai.</p>}
      {group.shared_mockups.length > 0 && (
        <div className="ng-shared">
          {group.shared_mockups.map(s => (
            <button key={s.file_id} type="button" className="ng-btn" onClick={() => openSheet(group.id, s.file_id)}
              data-testid={`nota-group-sheet-${s.file_id}`}>
              <Printer size={14}/> Lembar Mockup Bersama · {s.filename} · {s.users.length} nota</button>
          ))}
        </div>
      )}
    </div>
  );
}

function useGroup(orderNumber) {
  const [st, setSt] = useState({ loading: true, group: null, canManage: false, hidden: false });
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/nota-groups/by-order/${enc(orderNumber)}`, { withCredentials: true });
      setSt({ loading: false, group: r.data.group, canManage: !!r.data.can_manage, hidden: false });
    } catch { setSt({ loading: false, group: null, canManage: false, hidden: true }); }
  }, [orderNumber]);
  useEffect(() => { load(); }, [load]);
  return [st, load];
}

export function NotaGroupPanel({ orderNumber }) {
  const [st, reload] = useGroup(orderNumber);
  const [busy, setBusy] = useState(false);
  const run = async (fn, okTitle) => {
    setBusy(true);
    try { await fn(); await getMembership(true); await reload(); notify.success({ title: okTitle }); }
    catch (e) { notify.error({ title: "Grup Nota gagal diperbarui", message: errMsg(e) }); }
    finally { setBusy(false); }
  };
  const ask = (title) => notify.prompt({ title, message: "Masukkan nomor nota (pisahkan dengan koma bila lebih dari satu).",
    placeholder: "TMF-…", required: true, minLength: 3, primaryLabel: "Simpan", secondaryLabel: "Batal" });
  const create = async () => {
    const v = await ask("Buat Grup Nota");
    if (!v) return;
    const nums = v.split(/[,\s]+/).map(x => x.trim()).filter(Boolean);
    run(() => axios.post(`${API}/nota-groups`, { order_numbers: [orderNumber, ...nums] }, { withCredentials: true }), "Grup Nota dibuat");
  };
  const add = async () => {
    const v = await ask("Tambah Nota ke Grup");
    if (!v) return;
    run(async () => {
      for (const n of v.split(/[,\s]+/).map(x => x.trim()).filter(Boolean))
        await axios.post(`${API}/nota-groups/${enc(st.group.id)}/members`, { order_number: n }, { withCredentials: true });
    }, "Nota ditambahkan ke grup");
  };
  const remove = async (n) => {
    const reason = await notify.prompt({ title: `Keluarkan nota ${n} dari grup?`, message: "Alasan wajib diisi. Pembayaran & produksi nota tidak berubah.",
      required: true, minLength: 3, primaryLabel: "Keluarkan", secondaryLabel: "Batal", danger: true });
    if (!reason) return;
    run(() => axios.delete(`${API}/nota-groups/${enc(st.group.id)}/members/${enc(n)}`, { params: { reason }, withCredentials: true }), "Nota dikeluarkan dari grup");
  };
  if (st.loading || st.hidden || (!st.group && !st.canManage)) return null;
  return (
    <section className="ng-panel" data-testid="nota-group-panel">
      <div className="ng-head">
        <Layers size={16}/><strong>Grup Nota</strong>
        {st.group && <span className="ng-muted">{st.group.name ? `${st.group.name} · ` : ""}{st.group.notas.length} nota</span>}
        <span className="ng-spacer"/>
        {st.canManage && !st.group && <button type="button" className="ng-btn" disabled={busy} onClick={create} data-testid="nota-group-create-btn"><Plus size={14}/> Buat Grup Nota</button>}
        {st.canManage && st.group && <button type="button" className="ng-btn" disabled={busy} onClick={add} data-testid="nota-group-add-btn"><Plus size={14}/> Tambah Nota</button>}
      </div>
      <p className="ng-muted ng-note">Pengelompokan administratif — pembayaran, jumlah, barcode, dan tahap produksi tetap per nota.</p>
      {st.group && <GroupTable group={st.group} current={orderNumber} onRemove={st.canManage ? remove : null}/>}
    </section>
  );
}

export function NotaGroupGudangInfo({ orderNumber }) {
  const gid = useGroupId(orderNumber);
  return gid ? <GudangInfoInner orderNumber={orderNumber}/> : null;
}

function GudangInfoInner({ orderNumber }) {
  const [st] = useGroup(orderNumber);
  const [open, setOpen] = useState(false);
  if (!st.group) return null;
  const t = st.group.totals;
  return (
    <div className="ng-gudang" onClick={e => e.stopPropagation()} data-testid={`nota-group-gudang-${orderNumber}`}>
      <button type="button" className="ng-gudang-bar" onClick={() => setOpen(!open)} aria-expanded={open}
        data-testid={`nota-group-gudang-toggle-${orderNumber}`}>
        <Layers size={13}/> Grup Nota · {st.group.notas.length} nota · Total {t.pcs} pcs ·
        <span className="ng-ok"> Siap {t.siap_pcs}</span>{t.belum_pcs > 0 && <span className="ng-warn"> · Belum Selesai Mockup {t.belum_pcs}</span>}
      </button>
      {open && <GroupTable group={st.group} current={orderNumber}/>}
    </div>
  );
}

export function UseExistingMockupButton({ orderNumber, productId, onLinked }) {
  const gid = useGroupId(orderNumber);
  const [open, setOpen] = useState(false);
  if (!gid) return null;
  return (
    <div className="queue-mockup-row queue-mockup-row-link">
      <button type="button" className="btn-mockup-ghost btn-mockup-link btn-mockup-small" onClick={() => setOpen(true)}
        data-testid={`mockup-inline-use-existing-${productId}`}>
        <ImageIcon size={13}/> <span>Gunakan Mockup yang Sudah Ada</span></button>
      {open && <SourcePicker orderNumber={orderNumber} productId={productId} onClose={() => setOpen(false)}
        onLinked={() => { setOpen(false); onLinked && onLinked(); }}/>}
    </div>
  );
}

function SourcePicker({ orderNumber, productId, onClose, onLinked }) {
  const [st, setSt] = useState({ loading: true, sources: [], error: "" });
  const [busy, setBusy] = useState("");
  useEffect(() => {
    axios.get(`${API}/nota-groups/by-order/${enc(orderNumber)}/mockup-sources`, { withCredentials: true })
      .then(r => setSt({ loading: false, sources: r.data.sources || [], error: "" }))
      .catch(e => setSt({ loading: false, sources: [], error: errMsg(e) }));
  }, [orderNumber]);
  const pick = async (s) => {
    const ok = await notify.confirm({ title: "Gunakan mockup ini?",
      message: `Item ini akan memakai versi "${s.filename}" dari nota ${s.order_number}. Item TIDAK otomatis Selesai Mockup — tetap selesaikan tahap Mockup sesuai aturan. Revisi di nota sumber tidak mengubah versi yang dipakai di sini.`,
      primaryLabel: "Ya, gunakan", secondaryLabel: "Batal" });
    if (!ok) return;
    setBusy(s.file_id);
    try {
      await axios.post(`${API}/orders/${enc(orderNumber)}/products/${enc(productId)}/link-mockup`,
        { source_product_id: s.product_id, source_order_number: s.order_number, file_id: s.file_id }, { withCredentials: true });
      notify.success({ title: "Mockup Bersama ditautkan", message: `Memakai mockup nota ${s.order_number}.` });
      onLinked();
    } catch (e) { notify.error({ title: "Gagal menautkan mockup", message: errMsg(e) }); }
    finally { setBusy(""); }
  };
  return (
    <div className="ng-modal-bg" onClick={onClose} data-testid="mockup-source-picker">
      <div className="ng-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Pilih mockup dari nota lain">
        <div className="ng-head"><strong>Gunakan Mockup yang Sudah Ada</strong><span className="ng-spacer"/>
          <button type="button" className="ng-icon-btn" onClick={onClose} aria-label="Tutup" data-testid="mockup-source-close"><X size={16}/></button></div>
        <p className="ng-muted ng-note">Hanya mockup dari nota dalam Grup Nota yang sama.</p>
        {st.loading && <p className="ng-muted">Memuat…</p>}
        {st.error && <p className="ng-alert">{st.error}</p>}
        {!st.loading && !st.error && st.sources.length === 0 && <p className="ng-muted" data-testid="mockup-source-empty">Belum ada mockup di nota lain dalam grup.</p>}
        <div className="ng-sources">
          {st.sources.map(s => (
            <button key={s.file_id} type="button" className="ng-source" disabled={!!busy} onClick={() => pick(s)}
              data-testid={`mockup-source-${s.file_id}`}>
              <img src={`${API}/nota-groups/source-file/${enc(s.file_id)}/thumb?for_order=${enc(orderNumber)}`} alt=""
                loading="lazy" onError={e => { e.currentTarget.style.visibility = "hidden"; }}/>
              <span><b>Nota {s.order_number}</b> · {s.product_name} · {s.pcs} pcs</span>
              <span className="ng-muted">Versi: {s.filename} · {String(s.created_at || "").slice(0, 16).replace("T", " ")}</span>
              {busy === s.file_id && <span className="ng-muted">Menautkan…</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Iter 583 — Buat Nota: "Pesanan tambahan dari nota sebelumnya" (dipilih manual, tidak otomatis).
const waNorm = (v) => { const d = String(v || "").replace(/\D/g, ""); return d.startsWith("62") ? "0" + d.slice(2) : d; };
const nameNorm = (v) => String(v || "").toLowerCase().split(/\s+/).filter(Boolean).join(" ");
export function identityDiff(src, customerName, whatsapp) {
  if (!src) return [];
  const out = [];
  if (waNorm(src.whatsapp) && waNorm(whatsapp) && waNorm(src.whatsapp) !== waNorm(whatsapp)) out.push("Nomor WhatsApp");
  if (nameNorm(src.customer_name) !== nameNorm(customerName)) out.push("Nama pelanggan");
  return out;
}

export function NotaGroupSourcePicker({ value, onChange, customerName, whatsapp }) {
  const v = value || { enabled: false, source: null, confirmMismatch: false };
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ loading: false, items: [], error: "" });
  useEffect(() => {
    if (!v.enabled || v.source || q.trim().length < 3) { setRes({ loading: false, items: [], error: "" }); return; }
    const t = setTimeout(async () => {
      setRes(r => ({ ...r, loading: true }));
      try {
        const r = await axios.get(`${API}/nota-groups/source-search`, { params: { q: q.trim() }, withCredentials: true });
        setRes({ loading: false, items: r.data.results || [], error: "" });
      } catch (e) { setRes({ loading: false, items: [], error: errMsg(e) }); }
    }, 350);
    return () => clearTimeout(t);
  }, [q, v.enabled, v.source]);
  const diff = identityDiff(v.source, customerName, whatsapp);
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <div className="ng-picker" data-testid="nota-group-source-picker">
      <label className="ng-check">
        <input type="checkbox" checked={v.enabled} data-testid="nota-group-source-toggle"
          onChange={e => onChange({ enabled: e.target.checked, source: null, confirmMismatch: false })}/>
        <span>Pesanan tambahan dari nota sebelumnya</span>
      </label>
      {v.enabled && !v.source && (
        <div className="ng-picker-body">
          <input type="search" className="ng-input" placeholder="Cari nomor nota, nama pelanggan, atau nomor WhatsApp (min. 3 karakter)"
            value={q} onChange={e => setQ(e.target.value)} data-testid="nota-group-source-search"/>
          {res.loading && <p className="ng-muted">Mencari…</p>}
          {res.error && <p className="ng-alert">{res.error}</p>}
          {!res.loading && !res.error && q.trim().length >= 3 && res.items.length === 0 &&
            <p className="ng-muted" data-testid="nota-group-source-empty">Nota tidak ditemukan.</p>}
          {res.items.map(s => (
            <button key={s.order_number} type="button" className="ng-result" onClick={() => set({ source: s, confirmMismatch: false })}
              data-testid={`nota-group-source-result-${s.order_number}`}>
              <b>{s.order_number}</b> · {s.customer_name} · {s.whatsapp || "—"} · {s.pcs} pcs · {s.order_status}
              {s.group && <span className="ng-muted"> · sudah dalam grup ({s.group.members.length} nota)</span>}
            </button>
          ))}
        </div>
      )}
      {v.enabled && v.source && (
        <div className="ng-picked" data-testid="nota-group-source-selected">
          <div className="ng-head"><strong>Nota sumber: {v.source.order_number}</strong><span className="ng-spacer"/>
            <button type="button" className="ng-link-btn" onClick={() => set({ source: null, confirmMismatch: false })}
              data-testid="nota-group-source-change">Ganti</button></div>
          <p className="ng-muted">{v.source.customer_name} · {v.source.whatsapp || "—"}{v.source.company ? ` · ${v.source.company}` : ""} ·
            Tgl {v.source.order_date || "—"} · Deadline {v.source.deadline || "—"} · {v.source.order_status} · {v.source.pcs} pcs</p>
          <p className="ng-muted">{(v.source.products || []).join(" · ") || "—"}</p>
          <p className="ng-muted" data-testid="nota-group-source-plan">{v.source.group
            ? `Saat disimpan, nota baru bergabung ke grup yang sudah ada (${v.source.group.members.length} nota).`
            : "Saat disimpan, grup baru dibuat berisi nota sumber dan nota baru."} Mockup tidak disalin otomatis — desainer memilih sendiri per item.</p>
          {diff.length > 0 && (
            <label className="ng-alert ng-check" data-testid="nota-group-identity-warning">
              <input type="checkbox" checked={!!v.confirmMismatch} onChange={e => set({ confirmMismatch: e.target.checked })}
                data-testid="nota-group-identity-confirm"/>
              <span>{diff.join(" & ")} berbeda dengan nota sumber. Saya sudah memastikan pesanan ini memang tambahan dari nota tersebut.</span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}

// Dipanggil setelah nota tersimpan. Gagal → peringatan + Coba Lagi; tidak pernah menampilkan status terhubung palsu.
export async function attachToSourceGroup(orderNumber, link) {
  if (!link?.enabled || !link.source) return null;
  for (;;) {
    try {
      const r = await axios.post(`${API}/nota-groups/attach`, { source_order_number: link.source.order_number,
        order_number: orderNumber, confirm_identity_mismatch: !!link.confirmMismatch }, { withCredentials: true });
      await getMembership(true);
      return r.data.group;
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "object" ? d.message : (d || "Koneksi bermasalah.");
      const retry = await notify.confirm({ title: "Nota tersimpan, tetapi BELUM terhubung ke Grup Nota",
        message: `Nota ${orderNumber} sudah tersimpan dengan nomor resmi. Hubungan ke nota ${link.source.order_number} gagal: ${msg} Anda bisa mencoba lagi sekarang, atau menghubungkannya nanti dari Detail Nota → Grup Nota.`,
        primaryLabel: "Coba Lagi", secondaryLabel: "Nanti dari Detail Nota" });
      if (!retry) return null;
    }
  }
}
